import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { inject } from "@vercel/analytics";
import { HelmetProvider } from "react-helmet-async";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import AuthGate from "@/components/AuthGate";
import { ManagerConnectionProvider } from "@/components/ManagerConnectionProvider";
import SimpleTopBar from "@/components/SimpleTopBar";
import UseCasePage from "@/components/UseCasePage";
import ComparisonPage from "@/components/ComparisonPage";
import { getUseCaseBySlug } from "@/content/use-cases";
import {
  vsCursor,
  vsClaudeCode,
  vsCodex,
  vsDevin,
  vsJules,
} from "@/content/comparison";
import { supabase } from "@/lib/supabase";
import { lazy, Suspense, useEffect, useState } from "react";
import { initPostHog, posthog } from "@/lib/posthog";

const queryClient = new QueryClient();
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const LoginCliPage = lazy(() => import("@/pages/LoginCliPage"));
const BillingPage = lazy(() => import("@/pages/BillingPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const DocsPage = lazy(() => import("@/pages/DocsPage"));
const UseCasesSaaSFoundersPage = lazy(
  () => import("@/pages/UseCasesSaaSFoundersPage"),
);
const UseCaseAgenciesPage = lazy(() => import("@/pages/UseCaseAgenciesPage"));
const CollegeStudentsUseCasePage = lazy(
  () => import("@/pages/CollegeStudentsUseCasePage"),
);
const SmallBusinessUseCasePage = lazy(
  () => import("@/pages/SmallBusinessUseCasePage"),
);
const BackgroundAiCodingAgentsPage = lazy(
  () => import("@/pages/BackgroundAiCodingAgentsPage"),
);
const AiAgentOrchestrationPage = lazy(
  () => import("@/pages/AiAgentOrchestrationPage"),
);
const UseCasesIndexPage = lazy(() => import("@/pages/UseCasesIndexPage"));

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    posthog.capture("$pageview");
  }, [location.pathname]);
  return null;
}

function HomeRoute() {
  const [checked, setChecked] = useState(false);
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const { data } = await supabase.auth.getSession();
      if (!active) return;
      setAuthed(!!data.session);
      setChecked(true);
    };
    void load();
    return () => {
      active = false;
    };
  }, []);

  if (!checked) {
    return null;
  }

  return authed ? <Navigate to="/app" replace /> : <LandingPage />;
}

function UseCaseRoute() {
  const params = useParams<{ slug: string }>();
  const data = getUseCaseBySlug(params.slug ?? "");

  if (!data) {
    return <NotFound />;
  }

  return (
    <SimpleTopBar>
      <UseCasePage data={data} />
    </SimpleTopBar>
  );
}

function PricingRoute() {
  return (
    <SimpleTopBar>
      <PricingPage />
    </SimpleTopBar>
  );
}

function BillingRoute() {
  return (
    <SimpleTopBar>
      <BillingPage />
    </SimpleTopBar>
  );
}

const App = () => {
  useEffect(() => {
    inject();
    initPostHog();

    // Identify user if already logged in
    supabase.auth.getSession().then(({ data }) => {
      const user = data.session?.user;
      if (user) {
        posthog.identify(user.id, { email: user.email });
      }
    });

    // Keep PostHog identity in sync with auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === "SIGNED_IN" && session?.user) {
          posthog.identify(session.user.id, { email: session.user.email });
        } else if (event === "SIGNED_OUT") {
          posthog.reset();
        }
      },
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  return (
    <HelmetProvider>
      <ThemeProvider
        attribute="class"
        defaultTheme="dark"
        enableSystem={false}
        disableTransitionOnChange
      >
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <PageViewTracker />
              <Suspense fallback={null}>
                <Routes>
                  <Route path="/" element={<HomeRoute />} />
                  <Route path="/home" element={<LandingPage />} />
                  <Route
                    path="/setup"
                    element={<Navigate to="/docs?tab=setup" replace />}
                  />
                  <Route
                    path="/docs"
                    element={
                      <SimpleTopBar>
                        <DocsPage />
                      </SimpleTopBar>
                    }
                  />
                  <Route path="/login" element={<LoginCliPage />} />
                  <Route path="/login-cli" element={<LoginCliPage />} />
                  <Route path="/pricing" element={<PricingRoute />} />
                  <Route
                    path="/app"
                    element={
                      <AuthGate>
                        <ManagerConnectionProvider>
                          <Index />
                        </ManagerConnectionProvider>
                      </AuthGate>
                    }
                  />
                  <Route
                    path="/billing"
                    element={
                      <AuthGate>
                        <BillingRoute />
                      </AuthGate>
                    }
                  />
                  <Route
                    path="/dashboard"
                    element={<Navigate to="/app" replace />}
                  />
                  <Route
                    path="/use-cases"
                    element={
                      <SimpleTopBar>
                        <UseCasesIndexPage />
                      </SimpleTopBar>
                    }
                  />
                  <Route path="/use-cases/:slug" element={<UseCaseRoute />} />
                  <Route
                    path="/use-cases/ai-coding-agents-for-saas-founders"
                    element={
                      <SimpleTopBar>
                        <UseCasesSaaSFoundersPage />
                      </SimpleTopBar>
                    }
                  />
                  <Route
                    path="/use-cases/ai-agents-for-agencies"
                    element={
                      <SimpleTopBar>
                        <UseCaseAgenciesPage />
                      </SimpleTopBar>
                    }
                  />
                  <Route
                    path="/use-cases/ai-agents-for-small-business"
                    element={
                      <SimpleTopBar>
                        <SmallBusinessUseCasePage />
                      </SimpleTopBar>
                    }
                  />
                  <Route
                    path="/use-cases/ai-agents-for-college-students"
                    element={
                      <SimpleTopBar>
                        <CollegeStudentsUseCasePage />
                      </SimpleTopBar>
                    }
                  />
                  <Route
                    path="/background-ai-coding-agents"
                    element={
                      <SimpleTopBar>
                        <BackgroundAiCodingAgentsPage />
                      </SimpleTopBar>
                    }
                  />
                  <Route
                    path="/vs/cursor"
                    element={
                      <SimpleTopBar>
                        <ComparisonPage data={vsCursor} />
                      </SimpleTopBar>
                    }
                  />
                  <Route
                    path="/vs/claude-code"
                    element={
                      <SimpleTopBar>
                        <ComparisonPage data={vsClaudeCode} />
                      </SimpleTopBar>
                    }
                  />
                  <Route
                    path="/vs/codex"
                    element={
                      <SimpleTopBar>
                        <ComparisonPage data={vsCodex} />
                      </SimpleTopBar>
                    }
                  />
                  <Route
                    path="/vs/devin"
                    element={
                      <SimpleTopBar>
                        <ComparisonPage data={vsDevin} />
                      </SimpleTopBar>
                    }
                  />
                  <Route
                    path="/vs/jules"
                    element={
                      <SimpleTopBar>
                        <ComparisonPage data={vsJules} />
                      </SimpleTopBar>
                    }
                  />
                  <Route
                    path="/ai-agent-orchestration"
                    element={
                      <SimpleTopBar>
                        <AiAgentOrchestrationPage />
                      </SimpleTopBar>
                    }
                  />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;
